import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  DialogContentText,
  Container,
  Grid,
  CircularProgress,
  FormGroup,
  Checkbox,
  Divider
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePortal } from '../contexts/PortalContext';
import { Add as AddIcon, Warning as WarningIcon, Delete as DeleteIcon } from '@mui/icons-material';
import UserManagement from './UserManagement';
import ProductLocationsUpload from './ProductLocationsUpload';
import PlanogramManagement from './PlanogramManagement';
import ScanActivityView from './ScanActivityView';
import config from '../config';

function AdminPanel() {
  const { token } = useAuth();
  const { portalSettings, updatePortalSettings } = usePortal();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    firstName: '',
    lastName: '',
    isAdmin: false
  });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [portalName, setPortalName] = useState(portalSettings.portalName || 'Portal');

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      setError('Failed to fetch users');
    }
  };



  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchUsers();
      } catch (error) {
        console.error('Error loading admin data:', error);
      }
    };
    
    loadData();
  }, [token]);

  // Update portalName when portalSettings change
  useEffect(() => {
    setPortalName(portalSettings.portalName || 'Portal');
  }, [portalSettings.portalName]);

  const handleEditUser = (user) => {
    setSelectedUser({ ...user, password: '' });
    setDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/admin/users/${selectedUser._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(selectedUser)
      });
      
      if (response.ok) {
        setSuccess('User updated successfully');
        fetchUsers();
        setDialogOpen(false);
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (error) {
      setError('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await fetch(`${config.apiUrl}/admin/users/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          setSuccess('User deleted successfully');
          fetchUsers();
        } else {
          const data = await response.json();
          setError(data.error);
        }
      } catch (error) {
        setError('Failed to delete user');
      }
    }
  };

  const handleToggleRegistration = async () => {
    try {
      const result = await updatePortalSettings({
        registrationEnabled: !portalSettings.registrationEnabled
      });
      
      if (result.success) {
        setSuccess('Settings updated successfully');
      } else {
        setError(result.error || 'Failed to update settings');
      }
    } catch (error) {
      setError('Failed to update settings');
    }
  };

  const handleCreateUser = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/admin/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });
      
      if (response.ok) {
        setSuccess('User created successfully');
        fetchUsers();
        setCreateDialogOpen(false);
        setNewUser({
          username: '',
          password: '',
          email: '',
          firstName: '',
          lastName: '',
          isAdmin: false
        });
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (error) {
      setError('Failed to create user');
    }
  };

  const handleResetDatabase = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/temp/reset-database`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          secretKey: process.env.REACT_APP_ADMIN_SECRET || 'your_secret_key_here'
        })
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setError(data.error || 'Failed to reset database');
      }
    } catch (error) {
      console.error('Error resetting database:', error);
      setError('Failed to reset database');
    }
    setResetDialogOpen(false);
  };

  const handlePortalNameChange = async () => {
    try {
      const result = await updatePortalSettings({
        portalName
      });
      
      if (result.success) {
        setSuccess('Portal name updated successfully');
        // Refresh the portal settings to get the updated data
        window.location.reload();
      } else {
        setError(result.error || 'Failed to update portal name');
      }
    } catch (error) {
      setError('Failed to update portal name');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Panel
      </Typography>
      
      <Grid container spacing={3}>
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Grid>
        )}
        
        {success && (
          <Grid item xs={12}>
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <UserManagement />
        </Grid>
        
        <Grid item xs={12}>
          <ProductLocationsUpload />
        </Grid>

        <Grid item xs={12}>
          <PlanogramManagement />
        </Grid>

        <Grid item xs={12}>
          <ScanActivityView />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Registration Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Control whether new users can register for the portal. When disabled, the registration page will show a message asking users to contact an administrator.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                                  <Switch
                  checked={portalSettings.registrationEnabled}
                  onChange={handleToggleRegistration}
                  color="primary"
                />
                }
                label={portalSettings.registrationEnabled ? "Registration Enabled" : "Registration Disabled"}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {portalSettings.registrationEnabled 
                ? "Users can currently register for new accounts." 
                : "New user registration is currently disabled."}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Portal Name
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Set the name of your portal. This will be displayed in the navigation bar and other places throughout the application.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField
                label="Portal Name"
                value={portalName}
                onChange={(e) => setPortalName(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mr: 2 }}
              />
              <Button 
                variant="contained" 
                color="primary"
                onClick={handlePortalNameChange}
              >
                Update Name
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Time Off Management
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Clicking this button erases all data in the time off calendar. Real world use would be to press it at the end of the year to reset for the next year.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={async () => {
                if (window.confirm('Are you sure you want to clear ALL time off entries? This cannot be undone.')) {
                  try {
                    console.log('API URL:', config.apiUrl);
                    const clearUrl = `${config.apiUrl}/timeoff/clear-all`;
                    console.log('Making request to:', clearUrl);
                    
                    const response = await fetch(clearUrl, {
                      method: 'DELETE',
                      headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    
                    if (response.ok) {
                      const result = await response.json();
                      console.log('Clear result:', result);
                      setSuccess(result.message || 'All time off entries have been cleared');
                      setTimeout(() => setSuccess(''), 3000);
                    } else {
                      console.error('Error response:', response.status, response.statusText);
                      let errorMessage = 'Failed to clear entries';
                      
                      try {
                        const errorData = await response.json();
                        console.error('Error data:', errorData);
                        errorMessage = errorData.error || errorMessage;
                      } catch (parseError) {
                        console.error('Error parsing response:', parseError);
                      }
                      
                      setError(errorMessage);
                      setTimeout(() => setError(''), 5000);
                    }
                  } catch (error) {
                    console.error('Error clearing time off entries:', error);
                    setError('Failed to clear entries: ' + (error.message || 'Unknown error'));
                    setTimeout(() => setError(''), 5000);
                  }
                }
              }}
            >
              Clear All Time Off Entries
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default AdminPanel; 